import type { ITemplateResponse, ITemplateResponsePagination } from '@/types';

// A paginated endpoint that returns a page of items plus pagination meta.
type TFetchPage<T> = (
  page: number,
  perPage: number,
) => Promise<ITemplateResponse<T[], ITemplateResponsePagination>>;

type TLoadInChunksHandlers<T> = {
  // Called with the first page of items — use it to replace the current list
  // and unblock any first-paint loading state.
  onFirstPage: (items: T[]) => void;
  // Called for every subsequent page — use it to append to the list.
  onNextChunk: (items: T[]) => void;
};

/**
 * Loads a paginated collection in sequential fixed-size pages.
 *
 * Fetches the first page to learn the total page count, then walks the
 * remaining pages one at a time so we never flood the connection pool with a
 * parallel burst. Each page is handed back to the caller as it arrives, so the
 * consuming UI can fill in progressively. The routine is deliberately
 * state-agnostic: the caller owns loading flags and error handling and only
 * supplies how to store the first page and how to append the rest.
 * @param fetchPage - Fetches a single page given its number and page size.
 * @param handlers - Callbacks for the first page and each subsequent chunk.
 * @param handlers.onFirstPage - Receives the first page of items.
 * @param handlers.onNextChunk - Receives every subsequent page of items.
 * @param perPage - Number of items requested per page.
 * @returns A promise that resolves once every page has been fetched.
 */
export const loadInChunks = async <T>(
  fetchPage: TFetchPage<T>,
  { onFirstPage, onNextChunk }: TLoadInChunksHandlers<T>,
  perPage: number,
): Promise<void> => {
  const firstResponse = await fetchPage(1, perPage);
  onFirstPage(firstResponse.data.data);

  const pageCount = firstResponse.data.meta?.page_count ?? 1;

  for (let page = 2; page <= pageCount; page += 1) {
    const response = await fetchPage(page, perPage);
    onNextChunk(response.data.data);
  }
};
